import type { Block } from './content-types.js';

/**
 * An article.
 *
 * Articles are deliberately not part of `SiteContent`. That type requires every language
 * to carry every key, which is correct for the core pages (a half-translated privacy page
 * is a defect) and wrong for a growing set of articles, where publishing in one language
 * and translating later has to be possible without failing the build.
 *
 * The URL segment is not here: it lives in `article-slugs.ts`, which is the authority on
 * both the slug and which translations exist. See that file for why.
 */
export interface Article {
  /** Stable across languages, and the key into the slug map. Never appears in a URL. */
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly heading: string;
  readonly lede: string;
  /** One or two sentences for the index page. */
  readonly summary: string;
  /** ISO date, `YYYY-MM-DD`. Shown to readers and emitted as structured data. */
  readonly published: string;
  /** Only when a substantive revision has happened, never for a typo. */
  readonly updated?: string;
  readonly blocks: readonly Block[];
}

/** URL path for an article within its own language, before the locale prefix. */
export function articlePath(slug: string): string {
  return `/blog/${slug}`;
}

/** `/blog/some-slug` -> `some-slug`. Null for anything that is not an article path. */
export function articleSlugFromPath(path: string): string | null {
  const match = /^\/blog\/([a-z0-9-]+)\/?$/.exec(path);
  return match ? (match[1] ?? null) : null;
}
